'use client';

import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button } from "@chakra-ui/react";
import { IoMdArrowUp } from "react-icons/io";
import { useTranslations } from "next-intl";

type BackToTopButtonProps = {
  threshold?: number;
  bottom?: string;
  left?: string;
  size?: "sm" | "md" | "lg";
  ariaLabel?: string;
};

const BackToTopButton: React.FC<BackToTopButtonProps> = ({
  threshold = 400,
  bottom = "90px",
  left = "50%",
  size = "md",
  ariaLabel = "Scroll to top",
}) => {
  const t = useTranslations();
  const [visible, setVisible] = useState(false);
  const lastScrollY = useRef(0);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    
    if (currentScrollY < threshold) {
      setVisible(false)
      return;
    }
    
    if (currentScrollY < lastScrollY.current) {
      setVisible(true)
    } else {
      setVisible(false);
    }

    lastScrollY.current = currentScrollY
  }, [lastScrollY, threshold]);

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollToTop = useCallback(() => {
    if ("scrollBehavior" in document.documentElement.style) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // fallback для старых браузеров
    const start = window.pageYOffset;
    const duration = 400;
    let startTime: number | null = null;

    function step(timestamp: number) {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const t = Math.min(1, elapsed / duration);
      const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic
      window.scrollTo(0, Math.round(start * (1 - ease)));
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }, []);

  return (
    <Button
      aria-label={ariaLabel}
      colorPalette="white"
      backgroundColor="colorPalette.contrast"
      color="colorPalette.inverted"
      textStyle={{ base: "bold14", md: "bold16" }}
      onClick={scrollToTop}
      position="fixed"
      bottom={bottom}
      left={left}
      zIndex={9999}
      size={size}
      borderRadius="full"
      boxShadow="0px 0px 8px 0px #00000073"
      transform={visible ? "translate(-50%, 0)" : "translate(-50%, 12px)"}
      opacity={visible ? 1 : 0}
      pointerEvents={visible ? "auto" : "none"}
      transition="opacity 200ms ease, transform 200ms ease"
    >
      <IoMdArrowUp />
      {t('back_to_start')}
    </Button>
  );
};

export default BackToTopButton;
